// src/screens/CustomerVerificationScreen.js
import React, { useContext, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AppHeader from '../components/AppHeader';
import { AuthContext } from '../context/AuthContext';
import { dealerAPI } from '../services/api';

const CustomerVerificationScreen = () => {
  const { id } = useParams(); // appointment ID
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [selectedDoc, setSelectedDoc] = useState(null); // 'license' or 'insurance'

  useEffect(() => {
    // Check authorization
    if (!user) {
      navigate('/login');
      return;
    }
    if (user.role !== 'dealer_admin' && user.role !== 'dealer_staff') {
      navigate('/');
      return;
    }

    fetchCustomerDocuments();
  }, [user, id]);

  const fetchCustomerDocuments = async () => {
    try {
      setLoading(true);
      setError('');
      const { data: result } = await dealerAPI.getCustomerDocuments(id);
      setData(result);
    } catch (err) {
      console.error('Fetch customer documents error:', err);
      setError(err.response?.data?.message || 'Failed to load customer documents');
    } finally {
      setLoading(false);
    }
  };

  const getVerificationBadge = (status) => {
    switch (status) {
      case 'verified':
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
            <svg className="w-4 h-4 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            Verified
          </span>
        );
      case 'rejected':
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">
            <svg className="w-4 h-4 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            Rejected
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400">
            <svg className="w-4 h-4 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
            </svg>
            Pending Review
          </span>
        );
    }
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      'Pending': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
      'Confirmed': 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
      'Vehicle Ready': 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-400',
      'Checked In': 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
      'Completed': 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
      'Canceled': 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
      'No Show': 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
    };
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusColors[status] || 'bg-gray-100 text-gray-800'}`}>
        {status}
      </span>
    );
  };

  // Fetch the file with the session header and show it from memory, so the
  // login token never appears in a URL, log or browser history.
  const openDocument = async (type) => {
    try {
      const res = await dealerAPI.getCustomerDocumentFile(id, type);
      const url = URL.createObjectURL(res.data);
      setSelectedDoc({ type, url, mimeType: res.data.type });
    } catch (err) {
      setError(err.response?.status === 404 ? 'Document file not found' : 'Could not open the document');
    }
  };

  const closeDocument = () => {
    if (selectedDoc?.url) URL.revokeObjectURL(selectedDoc.url);
    setSelectedDoc(null);
  };

  const DocumentCard = ({ type, document }) => {
    const title = type === 'license' ? "Driver's License" : 'Insurance Card';
    const icon = type === 'license' ? '🪪' : '📋';

    if (!document || !document.fileName) {
      return (
        <div className="bg-white dark:bg-app-bg-secondary rounded-2xl p-6 border border-app-border-light dark:border-app-border">
          <div className="flex items-center gap-3 mb-4">
            <span className="text-3xl">{icon}</span>
            <h3 className="text-lg font-semibold text-app-text-light dark:text-app-text">{title}</h3>
          </div>
          <div className="text-center py-8">
            <div className="text-4xl mb-3">📭</div>
            <p className="text-app-text-secondary-light dark:text-app-text-secondary">
              Customer has not uploaded their {title.toLowerCase()} yet.
            </p>
          </div>
        </div>
      );
    }

    return (
      <div className="bg-white dark:bg-app-bg-secondary rounded-2xl p-6 border border-app-border-light dark:border-app-border">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <span className="text-3xl">{icon}</span>
            <h3 className="text-lg font-semibold text-app-text-light dark:text-app-text">{title}</h3>
          </div>
          {getVerificationBadge(document.verificationStatus)}
        </div>

        <div className="space-y-4">
          {/* Document info */}
          <div className="bg-app-bg-light dark:bg-app-bg rounded-xl p-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-app-text-secondary-light dark:text-app-text-secondary">File Name</p>
                <p className="font-medium text-app-text-light dark:text-app-text truncate">{document.fileName}</p>
              </div>
              <div>
                <p className="text-app-text-secondary-light dark:text-app-text-secondary">Uploaded</p>
                <p className="font-medium text-app-text-light dark:text-app-text">
                  {new Date(document.uploadedAt).toLocaleDateString()}
                </p>
              </div>
              <div>
                <p className="text-app-text-secondary-light dark:text-app-text-secondary">Type</p>
                <p className="font-medium text-app-text-light dark:text-app-text">
                  {document.mimeType?.includes('pdf') ? 'PDF Document' : 'Image'}
                </p>
              </div>
              <div>
                <p className="text-app-text-secondary-light dark:text-app-text-secondary">Status</p>
                <p className="font-medium text-app-text-light dark:text-app-text capitalize">
                  {document.verificationStatus}
                </p>
              </div>
            </div>
          </div>

          {/* Rejection reason if applicable */}
          {document.verificationStatus === 'rejected' && document.rejectionReason && (
            <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-4">
              <p className="text-sm font-medium text-red-700 dark:text-red-400">Rejection Reason:</p>
              <p className="text-sm text-red-600 dark:text-red-300">{document.rejectionReason}</p>
            </div>
          )}

          {/* View document button */}
          <button
            onClick={() => openDocument(type)}
            className="w-full py-3 px-4 rounded-xl text-center font-semibold bg-app-accent hover:bg-app-accent-hover text-black transition-colors"
          >
            View {title}
          </button>
        </div>
      </div>
    );
  };

  // Document viewer modal
  const DocumentViewer = ({ doc, onClose }) => {
    if (!doc) return null;

    const isPdf = doc.mimeType?.includes('pdf');

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80">
        <div className="relative w-full max-w-4xl max-h-[90vh] bg-white dark:bg-app-bg-secondary rounded-2xl overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-app-border-light dark:border-app-border">
            <h3 className="text-lg font-semibold text-app-text-light dark:text-app-text">
              {doc.type === 'license' ? "Driver's License" : 'Insurance Card'}
            </h3>
            <div className="flex items-center gap-2">
              <a
                href={doc.url}
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 rounded-xl text-sm font-medium bg-app-bg-light dark:bg-app-bg text-app-text-light dark:text-app-text hover:bg-app-bg-light/80"
              >
                Open in New Tab
              </a>
              <button
                onClick={onClose}
                className="p-2 rounded-xl hover:bg-app-bg-light dark:hover:bg-app-bg transition-colors"
              >
                <svg className="w-6 h-6 text-app-text-light dark:text-app-text" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-4 overflow-auto max-h-[calc(90vh-80px)]">
            {isPdf ? (
              <iframe
                src={doc.url}
                className="w-full h-[70vh] border-0 rounded-lg"
                title="Document Preview"
              />
            ) : (
              <img
                src={doc.url}
                alt={doc.type === 'license' ? "Driver's License" : 'Insurance Card'}
                className="max-w-full max-h-[70vh] mx-auto object-contain rounded-lg"
              />
            )}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex flex-col h-full">
        <AppHeader title="Customer Verification" subtitle="Loading..." />
        <div className="flex-1 flex items-center justify-center">
          <div className="w-8 h-8 border-4 border-app-accent border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col h-full">
        <AppHeader title="Customer Verification" subtitle="Error" />
        <div className="flex-1 p-4">
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-2xl p-6 text-center">
            <div className="text-4xl mb-3">⚠️</div>
            <p className="text-red-700 dark:text-red-400 font-medium mb-4">{error}</p>
            <button
              onClick={() => navigate(-1)}
              className="px-6 py-2 rounded-xl bg-app-bg-light dark:bg-app-bg text-app-text-light dark:text-app-text font-medium"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <AppHeader
        title="Customer Verification"
        subtitle="Review customer documents for test drive"
      />

      <div className="flex-1 overflow-y-auto p-4 space-y-4 max-w-4xl mx-auto w-full">
        {/* Back button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-app-text-secondary-light dark:text-app-text-secondary hover:text-app-text-light dark:hover:text-app-text transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Dashboard
        </button>

        {/* Appointment Info Card */}
        <div className="bg-white dark:bg-app-bg-secondary rounded-2xl p-6 border border-app-border-light dark:border-app-border">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-app-text-light dark:text-app-text">
              Appointment Details
            </h2>
            {data?.appointment && getStatusBadge(data.appointment.status)}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Customer Info */}
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-app-text-secondary-light dark:text-app-text-secondary uppercase tracking-wider">
                Customer Information
              </h3>
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-app-accent/20 rounded-full flex items-center justify-center text-xl font-bold text-app-accent">
                    {data?.customer?.name?.charAt(0).toUpperCase() || 'C'}
                  </div>
                  <div>
                    <p className="font-semibold text-app-text-light dark:text-app-text">
                      {data?.customer?.name || 'Unknown'}
                    </p>
                    <p className="text-sm text-app-text-secondary-light dark:text-app-text-secondary">
                      {data?.customer?.email || 'No email'}
                    </p>
                  </div>
                </div>
                {data?.customer?.phone && (
                  <p className="text-sm text-app-text-light dark:text-app-text">
                    <span className="text-app-text-secondary-light dark:text-app-text-secondary">Phone: </span>
                    {data.customer.phone}
                  </p>
                )}
              </div>
            </div>

            {/* Appointment Time */}
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-app-text-secondary-light dark:text-app-text-secondary uppercase tracking-wider">
                Scheduled Time
              </h3>
              <div className="bg-app-bg-light dark:bg-app-bg rounded-xl p-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-app-accent/20 rounded-xl flex items-center justify-center">
                    <svg className="w-6 h-6 text-app-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-semibold text-app-text-light dark:text-app-text">
                      {data?.appointment?.date
                        ? new Date(data.appointment.date).toLocaleDateString('en-US', {
                            weekday: 'long',
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                          })
                        : 'No date'}
                    </p>
                    <p className="text-sm text-app-text-secondary-light dark:text-app-text-secondary">
                      {data?.appointment?.time || 'No time'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Documents Section */}
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-app-text-light dark:text-app-text">
            Customer Documents
          </h2>
          <p className="text-sm text-app-text-secondary-light dark:text-app-text-secondary">
            Review the customer's uploaded documents to verify their identity and insurance before the test drive.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <DocumentCard type="license" document={data?.licenseDocument} />
            <DocumentCard type="insurance" document={data?.insuranceDocument} />
          </div>
        </div>

        {/* Verification Checklist */}
        <div className="bg-white dark:bg-app-bg-secondary rounded-2xl p-6 border border-app-border-light dark:border-app-border">
          <h3 className="text-lg font-semibold text-app-text-light dark:text-app-text mb-4">
            Verification Checklist
          </h3>
          <div className="space-y-3">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                className="w-5 h-5 rounded border-app-border-light dark:border-app-border text-app-accent focus:ring-app-accent"
              />
              <span className="text-app-text-light dark:text-app-text">
                Driver's license photo matches customer
              </span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                className="w-5 h-5 rounded border-app-border-light dark:border-app-border text-app-accent focus:ring-app-accent"
              />
              <span className="text-app-text-light dark:text-app-text">
                License is valid and not expired
              </span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                className="w-5 h-5 rounded border-app-border-light dark:border-app-border text-app-accent focus:ring-app-accent"
              />
              <span className="text-app-text-light dark:text-app-text">
                Insurance policy is active
              </span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                className="w-5 h-5 rounded border-app-border-light dark:border-app-border text-app-accent focus:ring-app-accent"
              />
              <span className="text-app-text-light dark:text-app-text">
                Insurance covers the vehicle being test driven
              </span>
            </label>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex gap-3 pb-6">
          <button
            onClick={() => navigate(-1)}
            className="flex-1 py-3 rounded-xl font-semibold bg-app-bg-light dark:bg-app-bg text-app-text-light dark:text-app-text border border-app-border-light dark:border-app-border"
          >
            Return to Dashboard
          </button>
        </div>
      </div>

      {/* Document Viewer Modal */}
      {selectedDoc && (
        <DocumentViewer doc={selectedDoc} onClose={closeDocument} />
      )}
    </div>
  );
};

export default CustomerVerificationScreen;
